import React, { FC, Fragment, useState, useMemo, memo } from 'react';
import _map from 'lodash/map';
import _take from 'lodash/take';
import _isObject from 'lodash/isObject';
import _isEmpty from 'lodash/isEmpty';
import { PreparePillsResult } from '../../shared/utils';
import { Pill } from '../../shared/components';
import { Label } from '../../shared/styles';
import { Container, PillListWrapper, PillList } from './FilterBasicBreadboxStyles';
import FilterBasicBreadboxPill from './FilterBasicBreadboxPill';
import FilterBasicBreadboxModal from './FilterBasicBreadboxModal';

const VISIBLE_PILLS_LENGTH_BY_DEFAULT = 6;

type Props = {
  isNotAppliedFilterPills?: boolean;
  label: string;
  pills: PreparePillsResult;
  onClear: (filterKey?: string, subKey?: string) => void;
  onFiltersValueClear?: (selectedFiltersValues: Record<string, any>) => void;
};

const FilterBasicBreadbox: FC<Props> = ({
  isNotAppliedFilterPills = false,
  label,
  pills,
  onClear,
  onFiltersValueClear,
}) => {
  const [isFilterBasicBreadboxModalOpened, setFilterBasicBreadboxModalOpened] = useState(false);

  const pillsKeys = Object.keys(pills);
  const visiblePillsKeys = useMemo(() => _take(pillsKeys, VISIBLE_PILLS_LENGTH_BY_DEFAULT), [pills]);
  const hiddenPillsCount = pillsKeys.length - visiblePillsKeys.length;

  const toggleFilterBasicBreadboxModal = (): void => {
    setFilterBasicBreadboxModalOpened(!isFilterBasicBreadboxModalOpened);
  };

  const pillKeyPrefix = isNotAppliedFilterPills ? `notApplied` : `applied`;

  const renderListNotePills = (key) => {
    return (
      <Fragment key={key}>
        {!_isEmpty(pills[key].value['list']) && (
          <FilterBasicBreadboxPill
            key={`${key}-list`}
            isNotAppliedFilterPills={isNotAppliedFilterPills}
            pillKey={`${pillKeyPrefix}-${key}-list`}
            pill={{ name: pills[key].name['list'], value: pills[key].value['list'] }}
            handleClear={(): void => {
              onClear(key, 'list');
            }}
          />
        )}
        {!_isEmpty(pills[key].value['note']) && (
          <FilterBasicBreadboxPill
            key={`${key}-note`}
            isNotAppliedFilterPills={isNotAppliedFilterPills}
            pillKey={`${pillKeyPrefix}-${key}-note`}
            pill={{ name: pills[key].name['note'], value: pills[key].value['note'] }}
            handleClear={(): void => {
              onClear(key, 'note');
            }}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Container data-testid={`filter-basic-breadbox-${isNotAppliedFilterPills ? 'notapplied' : 'applied'}`}>
      <Label $noMargin>{label}</Label>
      <PillListWrapper>
        <PillList>
          {_map(visiblePillsKeys, (key) =>
            _isObject(pills[key].name) ? (
              renderListNotePills(key)
            ) : (
              <FilterBasicBreadboxPill
                key={key}
                isNotAppliedFilterPills={isNotAppliedFilterPills}
                pillKey={`${pillKeyPrefix}-${key}`}
                pill={pills[key]}
                handleClear={(): void => {
                  onClear(key);
                }}
              />
            )
          )}
          {hiddenPillsCount > 0 && (
            <Pill
              isNotAppliedFilterPills={isNotAppliedFilterPills}
              showClearIcon={false}
              pillKey={`rest-filters-${isNotAppliedFilterPills ? 'notapplied' : 'applied'}`}
              text={`+${hiddenPillsCount}`}
              onClick={isNotAppliedFilterPills ? undefined : toggleFilterBasicBreadboxModal}
            />
          )}
        </PillList>
        {isFilterBasicBreadboxModalOpened && (
          <FilterBasicBreadboxModal
            data-testid={`filter-basic-breadbox-modal-${isNotAppliedFilterPills ? 'notapplied' : 'applied'}`}
            pills={pills}
            onFiltersValueClear={onFiltersValueClear}
            toggleModal={toggleFilterBasicBreadboxModal}
          />
        )}
      </PillListWrapper>
    </Container>
  );
};

export default memo<Props>(FilterBasicBreadbox);
